import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChiTietPhieuXuatKho, PhieuXuatKho, PhieuXuatKhoInput } from '@/types/schemas';
import { transformDataFromHoaDon } from '../utils/transform-data';
import { useRouter, useSearchParams } from '@/routing';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

type HoaDonBanHangForPhieuXuat = {
  uuid?: string;
  phieu_xuat_kho_uuid?: string;
  chi_tiet?: ChiTietPhieuXuatKho[];
  [key: string]: unknown;
};

export function useInvoiceParamsPresence() {
  const searchParams = useSearchParams();
  return useMemo(() => Boolean(searchParams.get('hdbh')), [searchParams]);
}

interface UseMultipleFormsArgs {
  addPhieuXuatKho: (data: PhieuXuatKhoInput) => Promise<PhieuXuatKho>;
  editPhieuXuatKho?: (uuid: string, data: PhieuXuatKhoInput) => Promise<PhieuXuatKho>;
}

export function useMultipleForms({ addPhieuXuatKho, editPhieuXuatKho }: UseMultipleFormsArgs) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { entity } = useAuth();

  const hasInvoiceParams = useInvoiceParamsPresence();

  // Get mode parameter from URL
  const mode = searchParams.get('mode');
  const [invoiceData, setInvoiceData] = useState<HoaDonBanHangForPhieuXuat | null>(); // Single invoice instead of array
  const [showMultipleForms, setShowMultipleForms] = useState<boolean>(hasInvoiceParams);
  const [existingPhieuXuatKhoData, setExistingPhieuXuatKhoData] = useState<PhieuXuatKho | null>();

  // Function to fetch hoa don ban hang data by UUID
  const fetchHoaDonBanHangByUuid = useCallback(
    async (uuid: string) => {
      if (!entity?.slug) return null;

      try {
        const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/${uuid}/`);
        return response.data as HoaDonBanHangForPhieuXuat;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  // Function to fetch existing phiếu xuất kho data by UUID
  const fetchPhieuXuatKhoByUuid = useCallback(
    async (uuid: string) => {
      if (!entity?.slug) return null;

      try {
        const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_KHO}/${uuid}/`);
        return response.data as PhieuXuatKho;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  // Handle URL params for creating phieu xuat kho from hoa don ban hang
  useEffect(() => {
    const hdbhParam = searchParams.get('hdbh');

    if (hdbhParam) {
      setShowMultipleForms(true);

      // Only handle single hdbh UUID (no comma-separated values)
      fetchHoaDonBanHangByUuid(hdbhParam).then(async invoice => {
        if (invoice) {
          setInvoiceData(invoice);

          // If in edit mode, also fetch existing phiếu xuất kho data
          if (mode === 'edit' && invoice.phieu_xuat_kho_uuid) {
            const existingPhieuXuatKho = await fetchPhieuXuatKhoByUuid(invoice.phieu_xuat_kho_uuid);
            setExistingPhieuXuatKhoData(existingPhieuXuatKho);
          }
        }
      });
    }
  }, [searchParams, entity?.slug, fetchHoaDonBanHangByUuid, fetchPhieuXuatKhoByUuid, mode]);

  // Close the chained form flow and return to the source invoice page.
  const handleNextStepNavigation = useCallback(async () => {
    setShowMultipleForms(false);
    setInvoiceData(null);
    setExistingPhieuXuatKhoData(null);

    const hdbhParam = searchParams.get('hdbh');
    if (hdbhParam) {
      router.push('/ban-hang/hoa-don-ban-ra/hoa-don-ban-hang');
    } else {
      router.back();
    }
  }, [router, searchParams]);

  // Multiple forms submit handler
  const handleMultipleFormSubmit = useCallback(
    async (data: PhieuXuatKhoInput) => {
      try {
        // Add nguon_hoa_don_id and tu_dong_tao if hdbh exists
        const dataWithNguonHoaDon = invoiceData?.uuid
          ? { ...data, nguon_hoa_don_id: invoiceData.uuid, tu_dong_tao: true }
          : data;

        if (mode === 'edit' && editPhieuXuatKho && existingPhieuXuatKhoData) {
          // In edit mode, update existing phiếu xuất kho
          const editDataWithOldValues = {
            ...dataWithNguonHoaDon,
            // Include old values from existing phiếu xuất kho
            i_so_ct: existingPhieuXuatKhoData.i_so_ct,
            so_ct: existingPhieuXuatKhoData.so_ct,
            ma_nk: existingPhieuXuatKhoData.ma_nk,
            ngay_ct: existingPhieuXuatKhoData.ngay_ct,
            ngay_lct: existingPhieuXuatKhoData.ngay_lct
          };

          if (existingPhieuXuatKhoData.uuid) {
            await editPhieuXuatKho(existingPhieuXuatKhoData.uuid, editDataWithOldValues);
          }
        } else {
          await addPhieuXuatKho(dataWithNguonHoaDon);
        }

        // Handle next parameter for navigation
        await handleNextStepNavigation();
      } catch (error) {
        return;
      }
    },
    [addPhieuXuatKho, editPhieuXuatKho, mode, existingPhieuXuatKhoData, handleNextStepNavigation, invoiceData]
  );

  // Close handler for multiple forms
  const handleCloseMultipleForms = useCallback(() => {
    setShowMultipleForms(false);
    setInvoiceData(null);
    setExistingPhieuXuatKhoData(null);

    // If hdbh parameter exists, go back to hoa-don-ban-hang, otherwise use router.back()
    const hdbhParam = searchParams.get('hdbh');
    console.log('Debug close - hdbhParam:', hdbhParam, 'searchParams:', searchParams.toString());

    if (hdbhParam) {
      console.log('Close: Navigating to hoa-don-ban-hang');
      router.push('/ban-hang/hoa-don-ban-ra/hoa-don-ban-hang');
    } else {
      console.log('Close: Using router.back()');
      router.back();
    }
  }, [router, searchParams]);

  // Build initial form data from current invoice + default tk_data
  let initialData = transformDataFromHoaDon(invoiceData);

  // If in edit mode, merge with existing phiếu xuất kho data
  if (mode === 'edit' && existingPhieuXuatKhoData) {
    initialData = {
      ...initialData,
      i_so_ct: existingPhieuXuatKhoData.i_so_ct,
      so_ct: existingPhieuXuatKhoData.so_ct,
      ma_nk: existingPhieuXuatKhoData.ma_nk,
      ngay_ct: existingPhieuXuatKhoData.ngay_ct,
      ngay_lct: existingPhieuXuatKhoData.ngay_lct
    } satisfies PhieuXuatKho;
  }

  return {
    showMultipleForms,
    invoiceData,
    initialData,
    handleMultipleFormSubmit,
    handleCloseMultipleForms,
    formMode: mode === 'edit' ? 'edit' : 'add'
  } as const;
}
